<script setup lang="ts">
import CurrencyText from '@uiKit/components/CurrencyText.vue'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import InfoSheetButton from '@uiKit/components/InfoSheetButton.vue'
import type { ReturnTile } from '../../property'
import { PROJECTED_RETURN_LABELS } from '../constants/investInfo'

// "Your projected returns" (Figma 32590:67057): net yield · appreciation ·
// annual ROI tiles, each with its ⓘ explainer. Captions are the app's
// ("ESTIMATED APPRECIATION"), not the property page's shorter ledger names.

interface Props {
  tiles: ReturnTile[]
}

defineProps<Props>()
</script>

<template>
  <Box v-if="tiles.length" direction="col" gap="4" class="returns">
    <Text type="body" weight="semibold" is="h3">Your projected returns</Text>
    <Box gap="6px">
      <Box
        v-for="tile in tiles"
        :key="tile.key"
        direction="col"
        justify="center"
        gap="2"
        class="returns__tile"
      >
        <Box align="start" justify="between" gap="1">
          <Text
            type="body"
            weight="semibold"
            color="muted"
            transform="uppercase"
            is="span"
            class="returns__label"
          >
            {{ PROJECTED_RETURN_LABELS[tile.key] }}
          </Text>
          <InfoSheetButton
            v-if="tile.sheet"
            :content="tile.sheet"
            :label="`About ${PROJECTED_RETURN_LABELS[tile.key]}`"
          />
        </Box>
        <Text type="heading" weight="bold" is="span"><CurrencyText :text="tile.value" /></Text>
      </Box>
    </Box>
  </Box>
</template>

<style scoped>
.returns {
  padding: 12px;
  border: 1px solid var(--p-primary-100);
  border-radius: 20px;
  background: linear-gradient(
    179deg,
    rgba(170, 160, 252, 0) 9%,
    rgba(237, 235, 245, 0.3) 79%,
    rgba(71, 247, 141, 0) 167%
  );
}

.returns__tile {
  flex: 1 1 0;
  min-width: 0;
  min-height: 80px;
  padding: 12px 8px;
  border: 1px solid var(--p-primary-100);
  border-radius: 12px;
  background: var(--p-surface-0);
}

.returns__label {
  letter-spacing: 0.14px;
  min-width: 0;
}
</style>
